import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useApolloClient} from "@apollo/client";
import {debounce} from "lodash";
import log from "loglevel";
import {DriverFilterInput, SearchDriversEnhancedDocument, SearchDriversEnhancedQuery} from "../../../generated/graphql";
import {makeQueryStringFilter} from "../../../utils/FilterUtils";

/** Raw driver from the Other-drivers query (vehicleDescription is resolved by the caller). */
export type OtherDriverRaw = {
  driverId: number;
  name: string | null;
  driverCode: string | null;
  vehicleTypeId: number | null;
  /** Comma-separated skill ids, as on `Driver.skills` (DP-3230 map skill check). */
  skills: string | null;
};

type UseOtherDriversPoolProps = {
  /** Reactive Step-1 scope filter (active + group/station), WITHOUT any search keyword. */
  filter: DriverFilterInput;
  pageSize?: number;
};

/**
 * DP-3039: paginated + searchable pool for the "Other drivers" Change scope. Fetches
 * `searchDriversEnhanced` one page at a time (infinite scroll) and searches server-side on
 * name/driverCode exactly like the Step-1 grid (`makeQueryStringFilter`). Accumulates pages,
 * guards against out-of-order responses, and resets when the scope filter or search changes.
 */
export const useOtherDriversPool = ({filter, pageSize = 25}: UseOtherDriversPoolProps) => {
  const client = useApolloClient();
  const [search, setSearch] = useState("");
  const [drivers, setDrivers] = useState<OtherDriverRaw[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  // Depend on the filter's VALUE, not its reference. The scope filter is rebuilt from provider
  // state (preferences/groups/stations) that often changes identity every render; keying the
  // fetch on the reference would refetch endlessly and the out-of-order guard would then discard
  // every response, leaving the pool empty. `filterRef` lets fetchPage read the latest filter
  // without listing it as a dep. (Same JSON-key pattern as ManifestDetailsDriverSelector.)
  const filterKey = JSON.stringify(filter);
  const filterRef = useRef(filter);
  filterRef.current = filter;

  const fetchPage = useCallback(
    async (offset: number, searchValue: string, append: boolean) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      const trimmed = searchValue.trim();
      const scopedFilter: DriverFilterInput = trimmed
        ? {...filterRef.current, queryString: makeQueryStringFilter(trimmed, ["name", "driverCode"])}
        : filterRef.current;
      try {
        const res = await client.query<SearchDriversEnhancedQuery>({
          query: SearchDriversEnhancedDocument,
          variables: {filter: scopedFilter, limit: pageSize, offset}
        });
        if (requestId !== requestIdRef.current) return; // a newer request superseded this one
        const page = (res.data?.searchDriversEnhanced?.items ?? [])
          .filter((driver): driver is NonNullable<typeof driver> => driver?.driverId != null)
          .map((driver) => ({
            driverId: driver.driverId!,
            name: driver.name ?? null,
            driverCode: driver.driverCode ?? null,
            vehicleTypeId: driver.vehicleTypeId ?? null,
            skills: driver.skills ?? null
          }));
        setTotal(res.data?.searchDriversEnhanced?.total ?? 0);
        setDrivers((prev) => (append ? [...prev, ...page] : page));
      } catch (error) {
        if (requestId === requestIdRef.current && !append) setDrivers([]);
        log.error("DP-3039 — failed to load the Other-drivers pool", error);
      } finally {
        if (requestId === requestIdRef.current) setLoading(false);
      }
    },
    // filterKey (value) drives refetch; the filter object itself is read via filterRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, filterKey, pageSize]
  );

  // Reset to page 0 whenever the scope filter or the search term changes.
  useEffect(() => {
    void fetchPage(0, search, false);
  }, [fetchPage, search]);

  const debouncedSetSearch = useMemo(() => debounce((value: string) => setSearch(value), 400), []);
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const hasMore = drivers.length < total;
  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    void fetchPage(drivers.length, search, true);
  }, [loading, hasMore, fetchPage, drivers.length, search]);

  return {drivers, loading, hasMore, onSearch: debouncedSetSearch, loadMore};
};
